import { useEffect, useState } from 'react';
import { GymsService } from '../../../services/gymsService';
import { getClubs } from '../../../services/clubsService';
import { REGIONES, getComunasPorRegion } from '../../../data/regionesYComunas';

/* Opciones de servicios (igual que opciones al crear/editar club) */
const OPCIONES_SERVICIOS = [
  'Entrenamiento',
  'Equipamiento',
  'Clases grupales',
  'Entrenamiento personalizado',
  'Powerlifting',
  'Bodybuilding',
  'Crossfit',
  'Strongman',
  'Entrenamiento funcional',
  'Competencias',
  'Comunidad'
];

const parseServicios = (texto) => {
  if (!texto || !String(texto).trim()) return [];
  return String(texto)
    .split(/[\n,;]/)
    .map(s => s.trim())
    .filter(Boolean);
};

const GymFormModal = ({ gym, onClose, onSuccess }) => {
  const [form, setForm] = useState({
    nombre: '',
    club_id: '',
    direccion: '',
    ciudad: '',
    region: '',
    telefono: '',
    email: '',
    estado: 'activo'
  });
  const [serviciosSeleccionados, setServiciosSeleccionados] = useState([]);
  const [serviciosOtros, setServiciosOtros] = useState('');

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* =========================
     CARGA DATOS AL EDITAR
  ========================= */
  useEffect(() => {
    if (gym) {
      setForm({
        nombre: gym.nombre ?? '',
        club_id: gym.club_id ? String(gym.club_id) : '',
        direccion: gym.direccion ?? '',
        ciudad: gym.ciudad ?? '',
        region: gym.region ?? '',
        telefono: gym.telefono ?? '',
        email: gym.email ?? '',
        estado: gym.estado ?? 'activo'
      });
      const partes = parseServicios(gym.descripcion ?? '');
      const seleccionados = partes.filter(p => OPCIONES_SERVICIOS.includes(p));
      const otros = partes.filter(p => !OPCIONES_SERVICIOS.includes(p)).join(', ');
      setServiciosSeleccionados(seleccionados);
      setServiciosOtros(otros);
    } else {
      setForm({
        nombre: '',
        club_id: '',
        direccion: '',
        ciudad: '',
        region: '',
        telefono: '',
        email: '',
        estado: 'activo'
      });
      setServiciosSeleccionados([]);
      setServiciosOtros('');
    }
  }, [gym]);

  /* =========================
     CARGAR CLUBS
  ========================= */
  useEffect(() => {
    const fetchClubs = async () => {
      try {
        const data = await getClubs(1, 100);
        setClubs(data?.items ?? []);
      } catch (err) {
        console.error('Error cargando clubs', err);
        setClubs([]);
      }
    };

    fetchClubs();
  }, []);

  /* =========================
     HANDLERS
  ========================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'region') next.ciudad = '';
      return next;
    });
  };

  const toggleServicio = (servicio) => {
    setServiciosSeleccionados(prev =>
      prev.includes(servicio)
        ? prev.filter(s => s !== servicio)
        : [...prev, servicio]
    );
  };

  const buildDescripcionServicios = () => {
    const otros = parseServicios(serviciosOtros);
    const todos = [...serviciosSeleccionados, ...otros];
    return todos.length ? todos.join(', ') : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const payload = {
      nombre: form.nombre.trim(),
      estado: form.estado,
      club_id: form.club_id ? Number(form.club_id) : null,
      direccion: form.direccion.trim() || null,
      ciudad: form.ciudad.trim() || null,
      region: form.region.trim() || null,
      telefono: form.telefono.trim() || null,
      email: form.email.trim() || null,
      descripcion: buildDescripcionServicios()
    };

    try {
      if (gym) {
        const res = await GymsService.editar(gym.id, payload);
        if (res?.error) throw new Error(res.error);
      } else {
        const res = await GymsService.crear(payload);
        if (res?.error) throw new Error(res.error);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error al guardar gym');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <style>{`
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal-card {
          background: #fff;
          padding: 24px;
          border-radius: 12px;
          width: 720px;
          max-width: 95%;
          max-height: 90vh;
          overflow-y: auto;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 14px 16px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          margin-bottom: 0;
        }

        .form-group.full {
          grid-column: span 2;
        }

        .servicios-opciones {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px 16px;
          margin-top: 6px;
        }

        .servicios-opciones label {
          font-weight: 400;
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }

        .servicios-opciones input[type="checkbox"] {
          width: 18px;
          height: 18px;
          margin: 0;
        }

        h3 {
          margin-top: 0;
          font-size: 20px;
          font-weight: 700;
        }

        label {
          font-size: 13px;
          font-weight: 600;
          margin-bottom: 4px;
          color: #374151;
        }

        input, select, textarea {
          padding: 10px 12px;
          border-radius: 8px;
          border: 1px solid #d1d5db;
          font-size: 14px;
        }

        textarea {
          min-height: 80px;
          resize: vertical;
        }

        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: #1a4d8c;
        }

        .error {
          color: #991b1b;
          background: #fee2e2;
          padding: 10px;
          border-radius: 8px;
          font-size: 13px;
          margin-bottom: 12px;
        }

        .actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 20px;
          grid-column: span 2;
        }

        .btn-primary {
          padding: 8px 16px;
          background: #1a4d8c;
          color: #fff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-ghost {
          padding: 8px 16px;
          background: #f4f6f8;
          color: #111;
          border: 1px solid #ddd;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
        }
      `}</style>

      <div className="modal-card">
        <h3>{gym ? 'Editar Gym' : 'Crear Gym'}</h3>

        {error && <div className="error full" style={{ gridColumn: 'span 2' }}>{error}</div>}

        <form onSubmit={handleSubmit} className="form-grid">
          <div className="form-group">
            <label>Nombre *</label>
            <input
              name="nombre"
              value={form.nombre}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Club asociado</label>
            <select
              name="club_id"
              value={form.club_id}
              onChange={handleChange}
            >
              <option value="">Sin club</option>
              {clubs.map(club => (
                <option key={club.id} value={club.id}>
                  {club.nombre}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group full">
            <label>Dirección</label>
            <input
              name="direccion"
              value={form.direccion}
              onChange={handleChange}
              placeholder="Ej: Av. Providencia 1234"
            />
          </div>

          <div className="form-group">
            <label>Región</label>
            <select
              name="region"
              value={form.region}
              onChange={handleChange}
            >
              <option value="">Seleccione región</option>
              {REGIONES.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Comuna</label>
            <select
              name="ciudad"
              value={form.ciudad}
              onChange={handleChange}
              disabled={!form.region}
            >
              <option value="">Seleccione comuna</option>
              {form.ciudad && form.region && !getComunasPorRegion(form.region).includes(form.ciudad) && (
                <option value={form.ciudad}>{form.ciudad}</option>
              )}
              {getComunasPorRegion(form.region).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Teléfono</label>
            <input
              name="telefono"
              type="tel"
              value={form.telefono}
              onChange={handleChange}
              placeholder="Ej: +56 9 1234 5678"
            />
          </div>

          <div className="form-group">
            <label>Contacto (email)</label>
            <input
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="Ej: contacto@gym.cl"
            />
          </div>

          <div className="form-group full">
            <label>Servicios</label>
            <div className="servicios-opciones">
              {OPCIONES_SERVICIOS.map(opcion => (
                <label key={opcion}>
                  <input
                    type="checkbox"
                    checked={serviciosSeleccionados.includes(opcion)}
                    onChange={() => toggleServicio(opcion)}
                  />
                  {opcion}
                </label>
              ))}
            </div>
            <input
              type="text"
              placeholder="Otros (separados por coma)"
              value={serviciosOtros}
              onChange={(e) => setServiciosOtros(e.target.value)}
              style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14 }}
            />
          </div>

          <div className="form-group">
            <label>Estado</label>
            <select
              name="estado"
              value={form.estado}
              onChange={handleChange}
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
              <option value="suspendido">Suspendido</option>
            </select>
          </div>

          <div className="actions">
            <button
              type="button"
              className="btn-ghost"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Guardando…' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GymFormModal;
